import * as React from 'react'
import { Markdown } from '@highlight/ui'
import { FadingScrollable } from '@renderer/chat/components'
import { useAudioNoteById } from '@renderer/hooks/audio-notes'
import { useAudioNoteTitle } from '../hooks.ts'
import { breakCellComponents, mathRehypePlugins, mathRemarkPlugins } from '../../markdown-extras'

import { ArtifactPreviewLoading } from './artifact-preview-loading'
import { ArtifactTimestamp } from './artifact-timestamp'

interface AudioNotePreviewProps {
  audioNoteId: string
}

export function AudioNotePreview({ audioNoteId }: AudioNotePreviewProps) {
  const { data: audioNote, isLoading, error } = useAudioNoteById(audioNoteId)

  const title = useAudioNoteTitle(audioNote)

  const cleanedSummary = React.useMemo(
    () => audioNote?.summary?.replace(/\n\nCaptured with \[Highlight\]\(https:\/\/highlightai\.com\)\.$/g, ''),
    [audioNote?.summary],
  )

  const content = React.useMemo(() => {
    // Show summary if available, otherwise fall back to transcript
    if (cleanedSummary) {
      return (
        <Markdown
          size="sm"
          remarkPlugins={mathRemarkPlugins}
          rehypePlugins={mathRehypePlugins}
          components={breakCellComponents}
        >
          {cleanedSummary}
        </Markdown>
      )
    }

    if (!audioNote?.transcript) return []

    return audioNote.transcript
      .split('\n')
      .reverse()
      .map((string, line) => {
        if (string.includes('other(s):') || string.includes('self:') || string.includes('other speaker:')) {
          const regex = string.includes('self:')
            ? /self:/
            : string.includes('other speaker:')
              ? /other speaker:/
              : /other\(s\):/
          const identifier = string.match(regex)?.[0] ?? ''
          const [speaker, text] = string.split(identifier)
          return (
            <div className={'font-350 flex flex-col'} key={`notes-line-${line}`}>
              <span className={'text-white/30'}>{speaker + identifier}</span>
              <span className={'leading-tight'}>{text}</span>
            </div>
          )
        }
        return <span key={`notes-line-${line}`}>{string}</span>
      })
  }, [cleanedSummary, audioNote?.transcript])

  if (isLoading) {
    return <ArtifactPreviewLoading />
  }

  if (error || !audioNote) {
    console.log('Error or no audio note:', { error, audioNote, audioNoteId })
    return (
      <div className="text-light/40">
        <p className="text-sm">Failed to load audio note</p>
        {error && <p className="mt-1 text-xs">ID: {audioNoteId}</p>}
      </div>
    )
  }

  return (
    <div className={'mb-6 h-48 max-h-48 max-w-64 text-[13px]'}>
      <div className={'flex flex-col gap-0.5 font-normal'}>
        <span className={'truncate font-medium'}>{audioNote.title ?? title}</span>
        <span className="text-light/40 text-xs">
          <ArtifactTimestamp date={audioNote.startedAt} />
        </span>
      </div>
      <FadingScrollable
        className="text-light/50 flex h-full flex-col gap-2 text-ellipsis whitespace-pre-line pt-2 leading-normal"
        onClick={(e) => e.stopPropagation()}
        fadeSize="20%"
      >
        {content}
      </FadingScrollable>
    </div>
  )
}
